import { useEffect, useMemo, useRef, useState } from 'react'

import { Job } from '../../../api/jobs'
import { Point } from '../../../api/types'
import iconLaunch from '../../../assets/images/icon_launch.svg'
import iconLaunchFlip from '../../../assets/images/icon_launch_flip.svg'
import iconZoomInBlue from '../../../assets/images/icon_zoom_in_blue.svg'
import iconZoomOutBlue from '../../../assets/images/icon_zoom_out_blue.svg'
import { useBuildingContext } from '../../../contexts/buildingContext'
import { PROGRESS_ITEM_GREY } from '../../../utils/progressItemDisplay'
import { Icon } from '../../common/Icon'
import { IconButton } from '../../common/IconButton'
import { LoadingIndicator } from '../../common/LoadingIndicator'
import { MapViewer } from '../../common/MapViewer'
import { Text } from '../../common/Text'
import { LayerManager } from '../progress/ProgressFloorTracker/LayerManager/LayerManager'

import { formatCaptureDate } from './reviewTaskFormat'
import {
  ReviewMinimapCard,
  ReviewMinimapHeader,
  ReviewMinimapLoadingOverlay,
  ReviewMinimapMap,
} from './styles'

interface ReviewMinimapProps {
  jobs: Job[]
  selectedItemIds: number[]
  selectedJobTypeIds?: number[]
  onSelectItemIds: (itemIds: number[], mode: 'toggle' | 'add') => void
  loading?: boolean
  onHoverItemId?: (itemId: number | null) => void
  embedded?: boolean
  yaw: number
  hfov?: number
  highlightedPointId?: string
  floorName?: string
  captureDate?: string | null
  points?: Point[]
  onClickPoint?: (pointSubId: number | string) => void
  onDoubleClickPoint?: (pointSubId: number | string) => void
}

export const ReviewMinimap = ({
  jobs,
  selectedItemIds,
  selectedJobTypeIds,
  onSelectItemIds,
  loading = false,
  onHoverItemId,
  embedded = false,
  yaw,
  hfov,
  highlightedPointId,
  floorName,
  captureDate,
  points,
  onClickPoint,
  onDoubleClickPoint,
}: ReviewMinimapProps) => {
  const { state: buildingState } = useBuildingContext()
  const [collapsed, setCollapsed] = useState(false)
  const [loadedImg, setLoadedImg] = useState('')
  const mapRef = useRef<MapViewer | null>(null)
  const floorData = buildingState.floorData
  const imageUrl = floorData?.imageUrl
  const mapPoints = useMemo(
    () => points || floorData?.points || [],
    [points, floorData?.points],
  )

  const selectedTypes = useMemo(() => {
    if (selectedJobTypeIds?.length) {
      return selectedJobTypeIds
    }
    const ids = new Set<number>()
    jobs.forEach(job => ids.add(job.type.job_type.id))
    return Array.from(ids)
  }, [jobs, selectedJobTypeIds])

  const imageLoaded = Boolean(imageUrl && loadedImg === imageUrl)

  const focusPoint = useMemo(() => {
    const point = mapPoints.find(
      (entry: { point_id: string | number; x?: number; y?: number }) =>
        String(entry.point_id) === String(highlightedPointId),
    )
    const x = Number(point?.x)
    const y = Number(point?.y)
    if (!point || !Number.isFinite(x) || !Number.isFinite(y)) {
      return null
    }
    return { x, y }
  }, [highlightedPointId, mapPoints])

  useEffect(() => {
    if (!focusPoint || loadedImg !== imageUrl) {
      return
    }
    mapRef.current?.recenterOnFocus()
  }, [focusPoint, highlightedPointId, imageUrl, loadedImg])

  if (collapsed && imageUrl) {
    return (
      <ReviewMinimapCard $collapsed $embedded={embedded}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setCollapsed(false)}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              setCollapsed(false)
            }
          }}
          style={{
            width: 36,
            height: 36,
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            opacity: 0.85,
          }}
        >
          <Icon icon={iconLaunchFlip} size={16} />
        </div>
      </ReviewMinimapCard>
    )
  }

  return (
    <ReviewMinimapCard $embedded={embedded}>
      <ReviewMinimapHeader>
        <div>
          <Text size={16} className="minimap-floor-heading">
            {floorName}
          </Text>
          {captureDate && (
            <div style={{ color: '#7E858E', fontSize: 12, marginTop: 4 }}>
              {formatCaptureDate(captureDate)}
            </div>
          )}
        </div>
        <Icon
          icon={iconLaunch}
          size={16}
          onClick={() => setCollapsed(true)}
          style={{ cursor: 'pointer' }}
        />
      </ReviewMinimapHeader>
      <ReviewMinimapMap>
        {(loading || !imageUrl || !imageLoaded) && (
          <ReviewMinimapLoadingOverlay>
            <LoadingIndicator fb size={20} />
          </ReviewMinimapLoadingOverlay>
        )}
        {imageUrl ? (
          <MapViewer
            ref={mapRef}
            key={imageUrl}
            selectedDateRange={null}
            image={imageUrl}
            points={mapPoints}
            highlightedPointId={highlightedPointId}
            showDirectionArrow
            rotation={yaw}
            fov={hfov}
            setLoadedImg={setLoadedImg}
            imageLoaded={imageLoaded}
            showPoints
            allPointsActive
            focusPoint={focusPoint}
            focusLayoutKey={`${highlightedPointId || ''}-${imageUrl}`}
            onClickMapPoint={point => onClickPoint?.(point.point_id)}
            onDoubleClickMapPoint={point =>
              onDoubleClickPoint
                ? onDoubleClickPoint(point.point_id)
                : onClickPoint?.(point.point_id)
            }
          >
            <LayerManager
              key={`${imageLoaded}-${jobs.length}-${selectedTypes.join(',')}`}
              canvasId="review-canvas"
              jobs={jobs}
              selectedJobTypes={selectedTypes}
              setTooltipItem={item => onHoverItemId?.(item?.id ?? null)}
              activeTracker=""
              displayZeroValues
              uninstalledColor={PROGRESS_ITEM_GREY}
              enableItemSelect
              queuedItemIds={selectedItemIds}
              onSelectItems={(itemIds, options) =>
                onSelectItemIds(
                  itemIds,
                  options?.mode === 'add' ? 'add' : 'toggle',
                )
              }
            />
          </MapViewer>
        ) : null}
        <IconButton
          icon={iconZoomInBlue}
          size={40}
          primary={false}
          onClick={() => mapRef.current?.zoomIn()}
          style={{ position: 'absolute', right: 8, bottom: 56 }}
        />
        <IconButton
          icon={iconZoomOutBlue}
          size={40}
          primary={false}
          onClick={() => mapRef.current?.zoomOut()}
          style={{ position: 'absolute', right: 8, bottom: 8 }}
        />
      </ReviewMinimapMap>
    </ReviewMinimapCard>
  )
}
